
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { AuditTrigger } from '@/types/audit';

interface AuditTriggersListProps {
  triggers: AuditTrigger[];
  onSelectTrigger: (trigger: AuditTrigger) => void;
}

export const AuditTriggersList: React.FC<AuditTriggersListProps> = ({ 
  triggers, 
  onSelectTrigger 
}) => {
  const getRiskBadgeColor = (risk: string) => {
    switch (risk) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-orange-100 text-orange-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'monitoring':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-4">Trigger Name</th>
                <th className="text-left p-4">Risk Level</th>
                <th className="text-left p-4">Category</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Compliance Score</th>
              </tr>
            </thead>
            <tbody>
              {triggers.length > 0 ? (
                triggers.map((trigger) => (
                  <tr 
                    key={trigger.id} 
                    className="border-b hover:bg-muted/50 cursor-pointer"
                    onClick={() => onSelectTrigger(trigger)}
                  >
                    <td className="p-4">
                      <div className="font-medium">{trigger.name}</div>
                      <div className="text-sm text-muted-foreground">{trigger.description}</div>
                    </td>
                    <td className="p-4">
                      <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(trigger.riskLevel)}`}>
                        {trigger.riskLevel.charAt(0).toUpperCase() + trigger.riskLevel.slice(1)}
                      </div>
                    </td>
                    <td className="p-4 capitalize">{trigger.category}</td>
                    <td className="p-4">
                      <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeVariant(trigger.status)}`}>
                        {trigger.status.charAt(0).toUpperCase() + trigger.status.slice(1)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center">
                        <span className={`mr-2 font-medium ${
                          trigger.complianceScore >= 90 ? 'text-green-600' :
                          trigger.complianceScore >= 80 ? 'text-yellow-600' : 'text-red-600'
                        }`}>{trigger.complianceScore}%</span>
                        <Progress value={trigger.complianceScore} 
                          className={`h-2 w-20 ${
                            trigger.complianceScore >= 90 ? 'bg-green-100' :
                            trigger.complianceScore >= 80 ? 'bg-yellow-100' : 'bg-red-100'
                          }`} />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-muted-foreground">
                    No audit triggers found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
